import type { Scene } from '@/types'

/**
 * Convert the last 8 hex chars of a UUID to a base36 string (6–7 chars).
 * Uses BigInt to avoid 32-bit integer overflow with 8 hex digits.
 */
function idToBase36(id: string): string {
  const hex = id.replace(/-/g, '').slice(-8)
  return BigInt(`0x${hex}`).toString(36)
}

/**
 * Generate a Notion-style slug from a scene: slugified name + base36 suffix.
 * e.g. "Opening Shot" with id "...6655a4b0" → "opening-shot-k7mxr"
 */
export function generateCanvasSlug(scene: Scene): string {
  const suffix = idToBase36(scene.id)
  const slugified = scene.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return slugified ? `${slugified}-${suffix}` : suffix
}

/**
 * Resolve a canvas slug back to a scene ID by matching the base36 suffix.
 * Works even if the name portion of the slug is stale (e.g. after a rename).
 */
export function resolveCanvasSlug(slug: string, scenes: Map<string, Scene>): string | null {
  const lastDash = slug.lastIndexOf('-')
  const suffix = lastDash >= 0 ? slug.slice(lastDash + 1) : slug

  let match: string | null = null
  for (const [id] of scenes) {
    if (idToBase36(id) === suffix) {
      if (match !== null) return null // Collision — fall back to defaults
      match = id
    }
  }
  return match
}

/**
 * Build a full canvas path: /p/{projectId}/{slug}?{params}
 * When sceneCount is 1, the slug is omitted for a cleaner URL.
 */
export function buildCanvasPath(
  projectId: string,
  scene: Scene | undefined,
  searchParams?: URLSearchParams,
  sceneCount?: number,
): string {
  const showSlug = scene && (sceneCount === undefined || sceneCount > 1)
  const slugPart = showSlug ? `/${generateCanvasSlug(scene)}` : ''
  const query = searchParams?.toString()
  return `/p/${projectId}${slugPart}${query ? `?${query}` : ''}`
}
